/**
 * Resumen del contenido de lote para las cards (Registros y detalle de lista).
 *
 * Fuentes, en orden:
 * - `lot.lot_content` de `getmylots?include=lot_content_summary`
 *   ({ title, operation, price, currency, images_count, videos_count, attachments_count, cover_url }).
 * - `lot.lot_user_content` de `getsharedlist` (arrays images / videos / attachments).
 *
 * Devuelve null si no hay nada para mostrar. No trae descripción (va en el detalle).
 * @see docs/maps/lot-content-summary.md
 */
import { formatListingPrice, OPERATION_LABEL } from './listingCoreConstants';
import { mapOperationFromApi } from './userLotFormUtils';

const count = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};
const arrLen = (value) => (Array.isArray(value) ? value.length : 0);

function fromShared(content) {
  const images = Array.isArray(content.images) ? content.images : [];
  const cover = images.find((img) => img?.is_cover) || images[0] || null;
  return {
    title: content.title,
    operation: content.operation ?? null,
    price_type: content.price_type ?? null,
    price: content.price,
    currency: content.currency,
    images_count: images.length,
    videos_count: arrLen(content.videos),
    attachments_count: arrLen(content.attachments),
    cover_url: (cover && (cover.url || cover.public_url || cover.image_url)) || null,
  };
}

/**
 * @param {object|null|undefined} lot
 * @returns {null|{ title: string, listing: string, images: number, videos: number, attachments: number, coverUrl: string|null }}
 */
export function getLotContentSummary(lot) {
  if (!lot || typeof lot !== 'object') return null;
  let raw = null;
  if (lot.lot_content && typeof lot.lot_content === 'object') raw = lot.lot_content;
  else if (lot.lot_user_content && typeof lot.lot_user_content === 'object') raw = fromShared(lot.lot_user_content);
  if (!raw) return null;

  const title = String(raw.title || '').trim();
  const priceStr = formatListingPrice(raw.price, raw.currency);
  const operation = mapOperationFromApi(raw);
  const opLabel = operation ? OPERATION_LABEL[operation] : null;
  // Mismo orden y formato que la línea de la UF: «USD 450.000 · Alquiler».
  const listing = [priceStr, opLabel].filter(Boolean).join(' · ');
  const images = count(raw.images_count);
  const videos = count(raw.videos_count);
  const attachments = count(raw.attachments_count);
  const coverUrl = raw.cover_url ? String(raw.cover_url) : null;

  if (!title && !listing && !images && !videos && !attachments) return null;
  return { title, listing, images, videos, attachments, coverUrl };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** «2 fotos · 2 videos · 1 adjunto». Solo los > 0. */
export function formatLotContentMedia(summary) {
  if (!summary) return '';
  return [
    summary.images ? plural(summary.images, 'foto', 'fotos') : null,
    summary.videos ? plural(summary.videos, 'video', 'videos') : null,
    summary.attachments ? plural(summary.attachments, 'adjunto', 'adjuntos') : null,
  ].filter(Boolean).join(' · ');
}

/** Tooltip del ícono: «Contenido de lote» + título, precio/operación y medios. */
export function formatLotContentTooltip(summary) {
  if (!summary) return 'Contenido de lote';
  return ['Contenido de lote', summary.title, summary.listing, formatLotContentMedia(summary)]
    .filter(Boolean)
    .join('\n');
}
